import SDKAdapter from '../core/adapter.js';
import { SDKFunctionalCollection } from '../core/collection.js';
import { SDKModel } from '../core/model.js';
import { SDKModelSchema } from '../core/schema.js';
import { nanoid } from '../core/transport.js';
import stylesheetSchema from '../schemas/stylesheet.json' assert { type: 'json' };
import * as Style from '../style/index.js';
import * as CSS from '../utils/css.js';
import { cloneDeep, isDeepEquals } from '../utils/object.js';
const FORMAT_VERSION = 30;
export function getStylesheetDefaults(sdk) {
    const now = new Date();
    return {
        id: 'default_screen',
        modifiedAt: now,
        createdAt: now,
        formatVersion: FORMAT_VERSION,
        source: Style.Presets.all,
        revision: 0,
        status: 'published'
    };
}
export class Stylesheets extends SDKAdapter {
    fetch(stylesheet) {
        const { libraryId } = stylesheet;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/stylesheets`);
    }
    get(stylesheet) {
        const { libraryId, status } = stylesheet;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/stylesheets?status=${status}`).then((stylesheets) => {
            return stylesheets[0];
        });
    }
    post(stylesheet) {
        return this.put(stylesheet);
    }
    put(stylesheet) {
        const { libraryId } = stylesheet;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/stylesheets`, {
            method: 'PUT',
            body: JSON.stringify(stylesheet)
        });
    }
    prune({ libraryId } = {}) {
        // not implemented on clientside
    }
}
class StylesheetModel extends SDKModel {
    get adapter() {
        return this.sdk.Stylesheets;
    }
    static getCSS(rules) {
        return CSS.toText((rules || []).concat(Style.Presets.internal));
    }
    get css() {
        return (this._css || (this._css = StylesheetModel.getCSS(this.source)));
    }
    set css(css) {
        this._css = css;
    }
    getId() {
        return this.libraryId + this.id + ':' + this.revision + ':' + this.status;
    }
    getSourcePath() {
        return `/${this.libraryId}/${this.status}.css`;
    }
    defineProperties() {
        super.defineProperties();
        this.sdk.utils.defineCollectionAccessor(this, 'rules', this.constructRules(), {}, this.getHiddenProps());
        this.sdk.utils.defineAccessor(this, 'source', {}, this.getHiddenProps());
        this.sdk.utils.defineAccessor(this, 'css', {}, this.getHiddenProps());
    }
    /** Rules is an observable collection that combines `source` styles + `internal` styles. Only source styles are saved on backend. */
    constructRules() {
        var stylesheet = this;
        const rules = new SDKFunctionalCollection(Style.Rule, (s) => s.details.id);
        // On each change fix & sort styles in collection
        rules.onCollectionChange = function () {
            this.splice(0, this.length, ...stylesheet.sortStyles(this));
            return this;
        };
        return rules;
    }
    sortStyles(rules = this.rules) {
        const fixedStyles = rules.slice();
        // make fonts show up on top, otherwise @imports break
        const desiredOrder = ['breakpoint', 'font'];
        // Reorder styles to bubble up the fonts
        const sortedStyles = fixedStyles.sort((a, b) => {
            const ax = desiredOrder.indexOf(a.type);
            const bx = desiredOrder.indexOf(b.type);
            if (a.type == 'breakpoint' && b.type == 'breakpoint') {
                return b.props.minWidth - a.props.minWidth;
            }
            return bx - ax;
        });
        return sortedStyles;
    }
    getHiddenProps() {
        return ['library', 'rules', 'css'];
    }
    getDefaults(sdk) {
        return getStylesheetDefaults(sdk);
    }
    getCollectionItems(style) {
        if (style.type !== 'collection')
            return [];
        return this.rules.filter((s) => s.details.collectionId === style.details.id && !s.details.isHidden && !s.details.elementId);
    }
    getStatus() {
        return this.status;
    }
    getBreakpointForWidth(width, breakpoints = Style.Set.filterByType(this.rules, 'breakpoint')) {
        if (!isFinite(width))
            width = 9999;
        return breakpoints.sort((a, b) => {
            return (Math.min(Math.abs(a.props.minWidth - width), Math.abs(a.props.maxWidth - width)) -
                Math.min(Math.abs(b.props.minWidth - width), Math.abs(b.props.maxWidth - width)));
        })[0];
    }
    isUpToDateWith(status) {
        return this.isSameContent(this.getCurrent(status));
    }
    isSameContent(stylesheet) {
        return isDeepEquals((stylesheet === null || stylesheet === void 0 ? void 0 : stylesheet.rules) || {}, this.rules) && (stylesheet === null || stylesheet === void 0 ? void 0 : stylesheet.formatVersion) == this.formatVersion;
    }
    /** Get highest revision of a stylesheet matching current status(es) if given */
    getCurrent(statuses) {
        return this.library.stylesheets.find((s) => statuses == null || [statuses || []].flat().includes(s.status));
    }
    getPath() {
        return `/libraries/${this.libraryId}/stylesheets/${this.id}`;
    }
    /** Return a current draft or create a new draft bumping up the revision */
    getDraft() {
        const current = this.getCurrent();
        if ((current === null || current === void 0 ? void 0 : current.status) == 'draft')
            return current;
        return current.produceNewVersion({
            modifiedAt: new Date(),
            revision: current.revision + 1,
            status: 'draft'
        });
    }
    getComparedStylesheet(status) {
        const validStylesheets = [this.getCurrent('saved'), this.getCurrent('staged'), this.getCurrent('published')];
        return validStylesheets.find((s) => s.status !== status && !s.isUpToDateWith(status) && s.status !== 'draft' // only 'saved', 'staged' and 'published'
        );
    }
    /** Register a new revision of a stylesheet with given properties based on this stylesheet  */
    produceNewVersion(props) {
        return this.library.stylesheets.addItem(this.clone(props));
    }
    /** Delete rule from a stylesheet, creates draft */
    deleteRule(rule) {
        const draft = this.getDraft();
        if (rule.type === 'collection') {
            draft.rules.slice().map((s) => {
                if (s.details.collectionId === rule.details.id) {
                    if (rule.props.type !== 'color' && rule.props.type !== 'breakpoint') {
                        // move things to default collection if exists
                        draft.rules.upsertItem({ ...s, details: { ...s.details, collectionId: `default-${s.type}` } });
                    }
                    else {
                        // for breakpoints and colors styles are deleted permanently
                        draft.rules.removeItem(s);
                    }
                }
            });
        }
        draft.rules.removeItem(rule);
        return;
    }
    /** Update or insert a rule in a stylesheet, creates draft */
    upsertRule(rule) {
        return this.getDraft().rules.upsertItem(rule);
    }
    /** Get last revision of a stylesheet that was ever saved */
    getLastSaved() {
        return this.getCurrent(['saved', 'staged', 'published']);
    }
    /** Save draft if it has changes over currently saved stylesheet */
    async saveDraft() {
        const draftToSave = this.getChangedDraft();
        if (draftToSave) {
            // commit changes from `rules` to `source` in current draft
            draftToSave.set({
                source: draftToSave.getSourceRules(),
                modifiedAt: new Date()
            });
            // create new saved version of a stylesheet with `saved` status. Revision will not be incremented.
            draftToSave
                .produceNewVersion({
                status: 'saved'
            })
                .save();
        }
    }
    async stage() {
        if (!this.isUpToDateWith('staged')) {
            // revert staged to published
            if (this.status === 'published') {
                const currentRevision = this.getCurrent().revision;
                await this.getCurrent('saved').produceNewVersion({ revision: currentRevision + 2 });
                return await this.produceNewVersion({ status: 'staged', revision: currentRevision + 1 });
                // stage saved/draft
            }
            else {
                return await this.produceNewVersion({ status: 'staged' }).save();
            }
        }
    }
    async publish() {
        // on publish do stage as well
        if (this.status !== 'staged')
            await this.stage();
        return await this.produceNewVersion({ status: 'published' }).save();
    }
    async revertDraftTo() {
        const revision = this.getCurrent().revision + 1;
        return await this.produceNewVersion({ status: 'saved', revision }).save();
    }
    /** Get draft if it has changes over currently saved version, will ignore meaningless drafts */
    getChangedDraft() {
        const draft = this.getCurrent();
        // there's no draft, no need to save
        if (draft && draft.status != 'draft')
            return this;
        const saved = this.getLastSaved();
        if (saved && !saved.isSameContent(draft)) {
            return draft;
        }
    }
    /** Filter out all internal styles that dont need to be saved on backend */
    getSourceRules(rules = this.rules.export()) {
        return Style.Set.fix(rules.concat(Style.Presets.internal)).filter((s) => !s.details.isInternal);
    }
    /** Copy stylesheet over to the other library. It will maintain revision numbering if the library already has stylesheet. Note that it's important that stylesheet is first saved and then added to collection, otherwise it will not be able to handle concurrent copyToLibrary calls and assign revisions correctly */
    async copyToLibrary(target, forceStatus = this.status) {
        var _a, _b, _c;
        const targetCurrentRevision = (_c = (_b = (_a = target.stylesheet) === null || _a === void 0 ? void 0 : _a.getCurrent('saved')) === null || _b === void 0 ? void 0 : _b.revision) !== null && _c !== void 0 ? _c : -1;
        return target.stylesheets.add(await target.stylesheets.save({
            source: this.source,
            status: forceStatus,
            formatVersion: this.formatVersion,
            revision: targetCurrentRevision + ['draft', 'published', 'staged', 'saved'].indexOf(this.status)
        }));
    }
    get source() {
        return this._source;
    }
    set source(rules) {
        const userRules = this.getSourceRules(rules || []);
        this._source = userRules;
        this._css = undefined;
        this.rules.setItems(userRules.concat(Style.Presets.internal));
    }
    static get version() {
        return FORMAT_VERSION;
    }
    static get schema() {
        return new SDKModelSchema(StylesheetModel, stylesheetSchema);
    }
    static getStyleNameWithoutIndex(name) {
        return name.replace(/ \((\d+)\)/gi, '');
    }
    static getIndexFromStyleName(name) {
        var _a, _b, _c;
        return (_c = (_b = (_a = name
            .match(/\((\d+)\)/gi)) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.replace('(', '')) === null || _c === void 0 ? void 0 : _c.replace(')', '');
    }
    static getStyleMaxIndex(style, styles) {
        let max = 0;
        styles.map((s) => {
            const sameName = StylesheetModel.getStyleNameWithoutIndex(s.details.title) ===
                StylesheetModel.getStyleNameWithoutIndex(style.details.title);
            if (s.type === style.type && sameName) {
                const index = StylesheetModel.getIndexFromStyleName(s.details.title);
                if (parseInt(index) > max) {
                    max = parseInt(index);
                }
            }
        });
        return max;
    }
    static generateNewComboIds(style) {
        style.props.inlines = style.props.inlines.map((i) => ({ ...i, id: nanoid(10) }));
        style.props.blocks = style.props.blocks.map((i) => ({ ...i, id: nanoid(10) }));
        style.props.texts = style.props.texts.map((i) => ({ ...i, id: nanoid(10) }));
        return style;
    }
    static sort(revisions) {
        return revisions.sort((a, b) => {
            return (Number(b.revision) - Number(a.revision) /** Higher revision first */ ||
                StylesheetModel.statuses.indexOf(a.status) -
                    StylesheetModel.statuses.indexOf(b.status) /** Published first, draft last */ ||
                Number(a.createdAt) - Number(b.createdAt));
        });
    }
    static onCollectionChange(versions) {
        return this.sort(versions);
    }
    duplicateRule(rule) {
        const newRules = StylesheetModel.getDuplicateRule(rule, this.rules);
        for (const newRule of newRules)
            this.upsertRule(newRule);
        return newRules[0];
    }
    static getDuplicateRule(rule, rules) {
        let copiedStyle = cloneDeep(rule);
        const newTitle = `${StylesheetModel.getStyleNameWithoutIndex(rule.details.title)} (${StylesheetModel.getStyleMaxIndex(rule, rules) + 1})`;
        copiedStyle.details = {
            ...rule.details,
            id: nanoid(10),
            title: newTitle,
            slug: null
        };
        let newStyles = [];
        for (const s of rules) {
            if (s.details.elementId !== rule.details.id)
                continue;
            let copiedCustomStyle;
            copiedCustomStyle = cloneDeep(s);
            copiedCustomStyle.details.elementId = copiedStyle.details.id;
            copiedCustomStyle.details.id = nanoid(10);
            newStyles.push(copiedCustomStyle);
            copiedStyle.props = Object.keys(copiedStyle.props).reduce((obj, prop) => {
                // @ts-ignore
                const ids = copiedStyle.props[prop];
                if (!ids.includes(s.details.id))
                    return { ...obj, [prop]: ids };
                return {
                    ...obj,
                    [prop]: [...ids.filter((id) => id !== s.details.id), copiedCustomStyle.details.id]
                };
            }, {});
        }
        if (rule.type === 'theme') {
            copiedStyle = StylesheetModel.generateNewComboIds(copiedStyle);
        }
        return [copiedStyle, ...newStyles];
    }
    // fixme: could narrow type here, spent too much time :(
    static getStyleTabSummary(style, form) {
        const properties = form.extractProperties(style.props);
        return form.validate(properties) ? 'Custom' : 'None';
    }
    getRuleErrors(rule, rules = this.rules) {
        var _a, _b;
        let errors = {};
        if (rule.details.override) {
            const parsed = CSS.parse(rule.details.override);
            if (((_a = parsed === null || parsed === void 0 ? void 0 : parsed[0]) === null || _a === void 0 ? void 0 : _a[0]) == 'error') {
                return (errors = { ...errors, details: { override: parsed[0][1] } });
            }
        }
        if (rule.type != 'font') {
            const otherStyle = Style.Set.findBySlug(rules, rule.type, Style.Rule.getSlug(rule, rules));
            if (otherStyle && otherStyle.details.id != rule.details.id) {
                errors = { ...errors, details: { title: 'Name needs to be unique' } };
            }
        }
        if (rule.type === 'font' ? !((_b = rule.props) === null || _b === void 0 ? void 0 : _b.familyName) : !rule.details.title) {
            errors = { ...errors, details: { title: 'Name is required' } };
        }
        return Object.keys(errors).length === 0 ? null : errors;
    }
    get Style() {
        return Style;
    }
    get ClassList() {
        return Style.ClassList;
    }
    get Context() {
        return Style.Context;
    }
    get Set() {
        return Style.Set;
    }
}
StylesheetModel.statuses = ['published', 'staged', 'saved', 'draft'];
export { StylesheetModel };
export { StylesheetModel as Sheet };
//# sourceMappingURL=stylesheets.js.map